<template>
  <div>
    <AppHeader />
    
    <!-- 主视觉区域 - 案例展示 -->
    <section class="relative h-96 flex items-center justify-center text-white bg-gradient-to-r from-purple-800 to-blue-900 pt-16 overflow-hidden">
      <div class="absolute inset-0">
        <img :alt="$t('caseStudies.hero.imageAlt')" class="w-full h-full object-cover opacity-40"
             src="/images/product/product-hero-bg.jpg">
      </div>
      <div class="relative z-10 text-center px-4 max-w-4xl">
        <h1 class="text-5xl md:text-6xl font-extrabold leading-tight mb-4 animate-fade-in-up">
          {{ $t('caseStudies.hero.title') }}
        </h1>
        <p class="text-xl md:text-2xl mb-6 max-w-3xl mx-auto animate-fade-in-up delay-200">
          {{ $t('caseStudies.hero.subtitle') }}
        </p>
      </div>
    </section>

    <!-- 筛选区域 -->
    <section class="py-12 px-6 md:px-12 bg-white">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-3xl font-bold mb-8 text-center text-gray-900">
          {{ $t('caseStudies.filter.title') }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <!-- 区域筛选 -->
          <div class="bg-gray-50 p-6 rounded-xl shadow-sm">
            <h3 class="text-xl font-semibold mb-4 text-gray-900 flex items-center">
              <i class="fas fa-globe-asia text-blue-600 mr-3"></i>
              {{ $t('caseStudies.filter.regionTitle') }}
            </h3>
            <div class="flex flex-wrap gap-3">
              <button
                v-for="region in regions"
                :key="region.value"
                @click="selectRegion(region.value)"
                :class="[
                  'px-5 py-2 rounded-full transition duration-300',
                  activeRegion === region.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                ]"
              >
                {{ region.label }}
              </button>
            </div>
          </div>

          <!-- 行业筛选 -->
          <div class="bg-gray-50 p-6 rounded-xl shadow-sm">
            <h3 class="text-xl font-semibold mb-4 text-gray-900 flex items-center">
              <i class="fas fa-industry text-purple-600 mr-3"></i>
              {{ $t('caseStudies.filter.industryTitle') }}
            </h3>
            <div class="flex flex-wrap gap-3">
              <button
                v-for="industry in industries"
                :key="industry.value"
                @click="selectIndustry(industry.value)"
                :class="[
                  'px-5 py-2 rounded-full transition duration-300',
                  activeIndustry === industry.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                ]"
              >
                {{ industry.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- 活跃筛选条件显示 -->
        <div class="flex flex-wrap items-center gap-3 mb-6">
          <span class="text-gray-600 font-medium">{{ $t('caseStudies.filter.currentFilter') }}:</span>
          <div class="flex flex-wrap gap-2">
            <span class="px-4 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center">
              {{ currentRegionLabel }}
              <button @click="selectRegion('all')" class="ml-2 text-blue-600 hover:text-blue-800">
                <i class="fas fa-times-circle"></i>
              </button>
            </span>
            <span class="px-4 py-1 bg-purple-100 text-purple-800 rounded-full text-sm flex items-center">
              {{ currentIndustryLabel }}
              <button @click="selectIndustry('all')" class="ml-2 text-purple-600 hover:text-purple-800">
                <i class="fas fa-times-circle"></i>
              </button>
            </span>
          </div>
          <button
            v-if="activeRegion !== 'all' || activeIndustry !== 'all'"
            @click="clearAllFilters"
            class="text-gray-500 hover:text-gray-700 text-sm ml-2"
          >
            {{ $t('caseStudies.filter.clearAll') }}
          </button>
        </div>
      </div>
    </section>

    <!-- 案例列表 -->
    <section class="py-10 px-6 md:px-12 bg-gray-50">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-3xl font-bold mb-10 text-center text-gray-900">
          {{ caseCountText }}
        </h2>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div
            v-for="caseItem in filteredCases"
            :key="caseItem.id"
            class="case-item bg-white rounded-xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
          >
            <div class="md:flex">
              <div class="md:w-2/5">
                <img :alt="caseItem.title" class="w-full h-full object-cover" :src="caseItem.image">
              </div>
              <div class="md:w-3/5 p-6">
                <div class="mb-5">
                  <h3 class="text-2xl font-bold text-gray-900 leading-tight mb-4">
                    {{ caseItem.title }}
                  </h3>
                  <div class="flex flex-wrap gap-2">
                    <span class="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-800 text-sm font-medium rounded-full border border-blue-100 leading-none max-w-full">
                      <i class="fas fa-location-dot text-xs"></i>
                      <span class="leading-5">{{ caseItem.regionLabel }}</span>
                    </span>
                    <span class="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-800 text-sm font-medium rounded-full border border-purple-100 leading-none max-w-full">
                      <i class="fas fa-layer-group text-xs"></i>
                      <span class="leading-5">{{ caseItem.industryLabel }}</span>
                    </span>
                  </div>
                </div>
                <div class="space-y-4 text-gray-700">
                  <div>
                    <h4 class="font-semibold text-lg text-gray-900 mb-2 flex items-center">
                      <i class="fas fa-info-circle text-blue-500 mr-2"></i>
                      {{ $t('caseStudies.caseList.projectBackground') }}
                    </h4>
                    <p>{{ caseItem.background }}</p>
                  </div>
                  <div>
                    <h4 class="font-semibold text-lg text-gray-900 mb-2 flex items-center">
                      <i class="fas fa-cogs text-green-500 mr-2"></i>
                      {{ $t('caseStudies.caseList.implementation') }}
                    </h4>
                    <ul class="list-disc pl-5 space-y-1 text-sm">
                      <li v-for="(item, index) in caseItem.implementation" :key="index">
                        {{ item }}
                      </li>
                    </ul>
                  </div>
                  <div class="grid grid-cols-3 gap-4 text-center text-sm">
                    <div v-for="(metric, index) in caseItem.metrics" :key="index" class="bg-gray-50 p-3 rounded-lg">
                      <div :class="['text-2xl font-bold mb-1', metric.color]">
                        {{ metric.value }}
                      </div>
                      <div class="text-gray-600">{{ metric.label }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 加载更多按钮 -->
        <div class="text-center mt-12">
          <button
            @click="loadMore"
            :disabled="isLoadingMore"
            class="px-8 py-3 bg-white text-purple-700 border border-purple-700 rounded-full text-lg font-semibold shadow-md hover:bg-purple-50 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="!isLoadingMore && !allLoaded">
              {{ $t('caseStudies.caseList.loadMore') }}
              <i class="fas fa-chevron-down ml-2"></i>
            </span>
            <span v-else-if="isLoadingMore">
              <i class="fas fa-spinner fa-spin mr-2"></i> {{ $t('caseStudies.caseList.loading') }}
            </span>
            <span v-else>
              {{ $t('caseStudies.caseList.noMore') }}
            </span>
          </button>
        </div>
      </div>
    </section>

    <!-- 咨询区域 -->
    <section class="py-20 px-6 md:px-12 bg-white text-gray-800">
      <div class="max-w-6xl mx-auto">
        <!-- 咨询引导部分 -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-extrabold mb-6">
            {{ $t('caseStudies.consultation.title') }}
          </h2>
          <p class="text-xl leading-relaxed max-w-3xl mx-auto opacity-80">
            {{ $t('caseStudies.consultation.description') }}
          </p>
        </div>

        <!-- 咨询按钮 -->
        <div class="text-center">
          <router-link
            to="/contact"
            class="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition duration-300 transform hover:scale-105"
          >
            {{ $t('caseStudies.consultation.button') }}
            <i class="fas fa-arrow-right ml-2"></i>
          </router-link>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const { t, tm } = useI18n()
const route = useRoute()

// 筛选选项 - 使用computed使其响应语言切换
const regions = computed(() => [
  { value: 'all', label: t('caseStudies.filter.regions.all') },
  { value: 'china', label: t('caseStudies.filter.regions.china') },
  { value: 'hongkong', label: t('caseStudies.filter.regions.hongkong') },
  { value: 'taiwan', label: t('caseStudies.filter.regions.taiwan') },
  { value: 'japan', label: t('caseStudies.filter.regions.japan') },
  { value: 'korea', label: t('caseStudies.filter.regions.korea') },
  { value: 'singapore', label: t('caseStudies.filter.regions.singapore') },
  { value: 'america', label: t('caseStudies.filter.regions.america') },
  { value: 'europe', label: t('caseStudies.filter.regions.europe') }
])

const industries = computed(() => [
  { value: 'all', label: t('caseStudies.filter.industries.all') },
  { value: 'manufacturing', label: t('caseStudies.filter.industries.manufacturing') },
  { value: 'finance', label: t('caseStudies.filter.industries.finance') },
  { value: 'healthcare', label: t('caseStudies.filter.industries.healthcare') },
  { value: 'retail', label: t('caseStudies.filter.industries.retail') },
  { value: 'government', label: t('caseStudies.filter.industries.government') },
  { value: 'professional', label: t('caseStudies.filter.industries.professional') }
])

// 当前筛选条件
const activeRegion = ref('all')
const activeIndustry = ref('all')

// 加载状态
const isLoadingMore = ref(false)
const allLoaded = ref(false)

// 案例数据 - 使用computed从翻译文件获取
const cases = computed(() => {
  const case1 = tm('caseStudies.cases.case1')
  const case2 = tm('caseStudies.cases.case2')
  const case3 = tm('caseStudies.cases.case3')
  const case4 = tm('caseStudies.cases.case4')
  const case5 = tm('caseStudies.cases.case5')
  
  return [
    {
      id: 1,
      region: 'japan',
      industry: 'manufacturing',
      regionLabel: case1.regionLabel,
      industryLabel: case1.industryLabel,
      title: case1.title,
      image: '/images/home/successScheme/JapaneseHybridCloud.png',
      background: case1.background,
      implementation: case1.implementation,
      metrics: case1.metrics.map((m, idx) => ({
        ...m,
        color: ['text-blue-600', 'text-green-600', 'text-purple-600'][idx]
      }))
    },
    {
      id: 2,
      region: 'singapore',
      industry: 'finance',
      regionLabel: case2.regionLabel,
      industryLabel: case2.industryLabel,
      title: case2.title,
      image: '/images/home/successScheme/DataSecurityInSingapore.png',
      background: case2.background,
      implementation: case2.implementation,
      metrics: case2.metrics.map((m, idx) => ({
        ...m,
        color: ['text-blue-600', 'text-green-600', 'text-purple-600'][idx]
      }))
    },
    {
      id: 3,
      region: 'korea',
      industry: 'healthcare',
      regionLabel: case3.regionLabel,
      industryLabel: case3.industryLabel,
      title: case3.title,
      image: '/images/home/successScheme/KoreanHealthcare.png',
      background: case3.background,
      implementation: case3.implementation,
      metrics: case3.metrics.map((m, idx) => ({
        ...m,
        color: ['text-blue-600', 'text-green-600', 'text-purple-600'][idx]
      }))
    },
    {
      id: 4,
      region: 'hongkong',
      industry: 'retail',
      regionLabel: case4.regionLabel,
      industryLabel: case4.industryLabel,
      title: case4.title,
      image: '/images/home/home-digital-transformation.jpg',
      background: case4.background,
      implementation: case4.implementation,
      metrics: case4.metrics.map((m, idx) => ({
        ...m,
        color: ['text-orange-600', 'text-emerald-600', 'text-purple-600'][idx]
      }))
    },
    {
      id: 5,
      region: 'china',
      industry: 'professional',
      regionLabel: case5.regionLabel,
      industryLabel: case5.industryLabel,
      title: case5.title,
      image: '/images/coreService/service-email-sorting.jpg',
      background: case5.background,
      implementation: case5.implementation,
      metrics: case5.metrics.map((m, idx) => ({
        ...m,
        color: ['text-blue-600', 'text-violet-600', 'text-green-600'][idx]
      }))
    }
  ]
})

// 当前筛选条件标签
const currentRegionLabel = computed(() => {
  return regions.value.find(r => r.value === activeRegion.value)?.label || t('caseStudies.filter.regions.all')
})

const currentIndustryLabel = computed(() => {
  return industries.value.find(i => i.value === activeIndustry.value)?.label || t('caseStudies.filter.industries.all')
})

// 筛选后的案例
const filteredCases = computed(() => {
  return cases.value.filter(caseItem => {
    const regionMatch = activeRegion.value === 'all' || caseItem.region === activeRegion.value
    const industryMatch = activeIndustry.value === 'all' || caseItem.industry === activeIndustry.value
    return regionMatch && industryMatch
  })
})

// 案例数量文本
const caseCountText = computed(() => {
  return `${t('caseStudies.caseList.resultCount')} (${filteredCases.value.length})`
})

// 选择区域
const selectRegion = (region) => {
  activeRegion.value = region
}

// 选择行业
const selectIndustry = (industry) => {
  activeIndustry.value = industry
}

// 清除所有筛选
const clearAllFilters = () => {
  activeRegion.value = 'all'
  activeIndustry.value = 'all'
}

// 加载更多
const loadMore = () => {
  isLoadingMore.value = true
  setTimeout(() => {
    isLoadingMore.value = false
    allLoaded.value = true
  }, 1500)
}

// 从URL参数初始化筛选条件
onMounted(() => {
  const { industry, region } = route.query
  
  if (industry && industry !== 'all') {
    activeIndustry.value = industry
  }
  
  if (region && region !== 'all') {
    activeRegion.value = region
  }
})
</script>

<style scoped>
@import '@/assets/css/main.css';
@import '@/assets/css/caseStudies.css';
</style>
